import { useQuery, useQueries, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { tripService } from '@/services/tripService';

const UPCOMING_STATUSES = ['Requested', 'Broadcasted', 'Accepted', 'Assigned', 'EnRoute', 'InProgress'];
const ONGOING_STATUSES = ['EnRoute', 'InProgress'];
const COMPLETED_STATUSES = ['Completed'];
const CANCELLED_STATUSES = ['Cancelled'];

const CATEGORY_STATUSES = {
  all: null,
  ongoing: ONGOING_STATUSES,
  upcoming: UPCOMING_STATUSES,
  completed: COMPLETED_STATUSES,
  cancelled: CANCELLED_STATUSES,
};

export function categoryForStatus(status) {
  if (!status) return null;
  if (ONGOING_STATUSES.includes(status)) return 'ongoing';
  if (COMPLETED_STATUSES.includes(status)) return 'completed';
  if (CANCELLED_STATUSES.includes(status) || status === 'NoShow') return 'cancelled';
  return 'upcoming';
}

export function useTrips(filters = {}) {
  return useQuery({
    queryKey: ['trips', filters],
    queryFn: async () => {
      const result = await tripService.getTrips(filters);
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
  });
}

// `live` → poll every 2s so the page reflects server-side changes (status, driver,
// payment hold) without a reload. Paused while a cancel modal is open (cancel gate).
// Edit/confirm pass live too: their un-submitted edits live in a SEPARATE local overlay
// (useEditTrip), so polling this server copy never touches them.
// `pollMs` is the slow lane: a caller that needs to notice a server-side change
// eventually, not immediately. The flight-arrival watch uses it — `expected_landing_at`
// moves when the backend re-polls the flight, and a timer armed against a stale value
// fires early on exactly the delayed flight it most needs to get right. Ignored while
// `live` is set, which is already faster.
export function useTripDetails(tripId, { live = false, enabled = true, pollMs = 0 } = {}) {
  const queryClient = useQueryClient();
  const cancelPaused = useCancelGatePaused();

  return useQuery({
    queryKey: ['trip', tripId],
    queryFn: async () => {
      const result = await tripService.getTripDetails(tripId);
      if (!result.success) throw new Error(result.error);
      return { trip: result.data, paymentMethods: result.paymentMethods };
    },
    // `enabled` lets a caller gate the query (e.g. the cancel modal only fetches while
    // it's open, so a trips list doesn't fire one poll per row).
    enabled: enabled && !!tripId,
    refetchInterval: cancelPaused ? false : (live ? 2000 : (pollMs || false)),
    placeholderData: () => {
      const allTripsQueries = queryClient.getQueriesData({ queryKey: ['trips'] });
      for (const [, data] of allTripsQueries) {
        const match = data?.trips?.find(t => String(t.id) === String(tripId));
        if (match) return { trip: match, paymentMethods: match.passenger?.payment_methods || [] };
      }
      return undefined;
    },
  });
}

/**
 * Full detail for SEVERAL trips at once — same query key as `useTripDetails`, so a trip
 * another screen already polls is one request, not two. Returns the trips (null while
 * one has not loaded), in the order of `tripIds`.
 *
 * For the passenger's location share: the ongoing LIST is not guaranteed to carry
 * `flight.flight_status`, and a from-airport trip may only share once its flight landed.
 */
export function useTripsDetails(tripIds, { pollMs = 0 } = {}) {
  return useQueries({
    queries: tripIds.map((tripId) => ({
      queryKey: ['trip', tripId],
      queryFn: async () => {
        const result = await tripService.getTripDetails(tripId);
        if (!result.success) throw new Error(result.error);
        return { trip: result.data, paymentMethods: result.paymentMethods };
      },
      refetchInterval: pollMs || false,
    })),
  }).map((q) => q.data?.trip || null);
}

const CATEGORY_LIST_FETCHERS = {
  ongoing: (opts) => tripService.getOngoingTrips(opts),
  upcoming: (opts) => tripService.getUpcomingTrips(opts),
  completed: (opts) => tripService.getCompletedTrips(opts),
  cancelled: (opts) => tripService.getCancelledTrips(opts),
};

// Live list options — used by every trips list/count so the UI stays in sync with the
// backend without a reload. Cached forever, re-polled every 2s, and (via React Query's
// default structuralSharing) the UI updates ONLY when the response actually changes:
//  - refetchInterval 2s → re-poll every two seconds (pauses when the tab is hidden).
//  - gcTime Infinity → keep the response cached forever (until a full page reload).
//  - staleTime Infinity → the poll is the only refresher; mount/focus just serve cache instantly.
//  - structuralSharing (React Query default) → when a poll returns data deep-equal to the cache,
//    the SAME reference is kept, so components DON'T re-render; only real changes update the UI.
const LIVE_LIST_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchInterval: 2000, // two seconds
  placeholderData: keepPreviousData,
};

// Cancel gate — pause ALL trips polling while a cancel modal is open. Otherwise the
// 2s poll drops the just-cancelled trip from its list, unmounting the row (and with
// it CancelRide's own "Ride Cancelled" success screen) before the user can read it.
// CancelRide raises this on open and lowers it on close; the on-close invalidation
// then refetches and removes the trip cleanly.
let cancelGateCount = 0;
const cancelGateListeners = new Set();
export function setCancelGate(open) {
  cancelGateCount = Math.max(0, cancelGateCount + (open ? 1 : -1));
  cancelGateListeners.forEach((l) => l());
}
// True while any cancel modal is open — shared by every live query so a poll can't
// unmount a CancelRide success screen mid-flow.
export function useCancelGatePaused() {
  return useSyncExternalStore(
    (cb) => { cancelGateListeners.add(cb); return () => cancelGateListeners.delete(cb); },
    () => cancelGateCount > 0,
    () => false,
  );
}
function useLiveListOptions() {
  const paused = useCancelGatePaused();
  return paused ? { ...LIVE_LIST_OPTIONS, refetchInterval: false } : LIVE_LIST_OPTIONS;
}

export function useCategoryTrips(category, { page = 1, perPage = 10 } = {}) {
  const statuses = CATEGORY_STATUSES[category];
  const dedicatedFetcher = CATEGORY_LIST_FETCHERS[category];

  return useQuery({
    queryKey: ['trips', 'category', category, { page, perPage }],
    queryFn: async () => {
      if (dedicatedFetcher) {
        const result = await dedicatedFetcher({ page, perPage });
        if (!result.success) throw new Error(result.error);
        return { trips: result.data, pagination: result.pagination };
      }
      const filters = { page, per_page: perPage, sort_order: 'desc' };
      if (statuses && statuses.length === 1) {
        filters.status = statuses[0];
      } else if (statuses && statuses.length > 1) {
        filters['status[]'] = statuses;
      }
      const result = await tripService.getTrips(filters);
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
    // Poll like the dedicated lists — the home BookingHistory tabs use this hook, and
    // must stay live (e.g. a cancelled trip leaves "Upcoming") the same way "Your Next
    // Ride" (useUpcomingTrips) does, without a reload or an explicit invalidation.
    ...useLiveListOptions(),
  });
}

export function useOngoingTrips({ page = 1, perPage = 10 } = {}) {
  return useQuery({
    queryKey: ['trips', 'ongoing', { page, perPage }],
    queryFn: async () => {
      const result = await tripService.getOngoingTrips({ page, perPage });
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
    ...useLiveListOptions(),
  });
}

export function useUpcomingTrips({ page = 1, perPage = 10 } = {}) {
  return useQuery({
    queryKey: ['trips', 'upcoming', { page, perPage }],
    queryFn: async () => {
      const result = await tripService.getUpcomingTrips({ page, perPage });
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
    ...useLiveListOptions(),
  });
}

export function useCompletedTrips({ page = 1, perPage = 10 } = {}) {
  return useQuery({
    queryKey: ['trips', 'completed', { page, perPage }],
    queryFn: async () => {
      const result = await tripService.getCompletedTrips({ page, perPage });
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
    ...useLiveListOptions(),
  });
}

export function useCancelledTrips({ page = 1, perPage = 10 } = {}) {
  return useQuery({
    queryKey: ['trips', 'cancelled', { page, perPage }],
    queryFn: async () => {
      const result = await tripService.getCancelledTrips({ page, perPage });
      if (!result.success) throw new Error(result.error);
      return { trips: result.data, pagination: result.pagination };
    },
    ...useLiveListOptions(),
  });
}

export function useCategoryCount(category) {
  const statuses = CATEGORY_STATUSES[category];
  const dedicatedFetcher = CATEGORY_LIST_FETCHERS[category];

  return useQuery({
    queryKey: ['trips', 'count', category],
    queryFn: async () => {
      if (dedicatedFetcher) {
        const result = await dedicatedFetcher({ page: 1, perPage: 1 });
        if (!result.success) throw new Error(result.error);
        return result.pagination?.total ?? (result.data?.length || 0);
      }
      const filters = { per_page: 1, without_passenger: true, without_polyline: true, without_route_legs: true, without_pricing: true, without_stop_overs: true };
      if (statuses && statuses.length === 1) {
        filters.status = statuses[0];
      } else if (statuses && statuses.length > 1) {
        filters['status[]'] = statuses;
      }
      const result = await tripService.getTrips(filters);
      if (!result.success) throw new Error(result.error);
      return result.pagination?.total ?? (result.data?.length || 0);
    },
    // Poll so the tab badges stay in sync with the live lists — otherwise a cancelled
    // trip leaves the list but the "Upcoming" count still reads 1.
    ...useLiveListOptions(),
  });
}

